import React, { useState } from "react";
import { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Link } from "gatsby";

import { ArrowDown } from "../assets/icons/arrowDownIcon";

import { headerTheme } from "./Header";
import { ForwardArrow } from "../assets/icons";
import jenAiDataChatsBanner from "../assets/images/jen-ai-data-chats-banner.png";
import aiAfterHoursBanner from "../assets/images/ai-after-hours-banner.png";
import deepLearningLeadersBanner from "../assets/images/deep-learning-leaders-nav.avif";

export const resources = [
  {
    name: "Blog",
    href: "/blog/",
    external: false,
  },
  {
    name: "Research papers",
    href: "/research-reports/",
    external: false,
  },
  {
    name: "Documentation",
    href: "https://docs.encord.com/",
    external: true,
  },
  {
    name: "Glossary",
    href: "/glossary/",
    external: false,
  },
  {
    name: "Learning hub",
    href: "/learning-hub/",
    external: false,
  },
  {
    name: "Webinars",
    href: "/webinars/",
    external: false,
  },
];

const engageItems = [
  {
    id: "ai-data-chats",
    name: "AI Data Chats",
    description:
      "Watch Jen's series of interviews with key thought leaders in the AI data space.",
    detailTitle: "AI Data Chats",
    detailDescription:
      "Watch Jen's series of interviews with key thought leaders in the AI data space.",
    buttonText: "Watch now",
    href: "/ai-data-chats/",
    imageSrc: jenAiDataChatsBanner,
    external: false,
  },
  {
    id: "ai-after-hours",
    name: "AI After Hours",
    description:
      "Monthly meet ups featuring lightning talks from AI leaders and industry experts.",
    detailTitle: "AI After Hours",
    detailDescription:
      "Monthly meetups featuring lightning talks from AI leaders and industry experts.",
    buttonText: "See all events",
    href: "/ai-after-hours/",
    imageSrc: aiAfterHoursBanner,
    external: false,
  },
  {
    id: "build-vs-buy",
    name: "Build vs Buy Calculator",
    description:
      "Calculate the true cost of building your own AI data pipeline vs using Encord.",
    detailTitle: "Build vs Buy Calculator",
    detailDescription:
      "Calculate the true cost of building your own AI data pipeline vs using Encord.",
    buttonText: "Start calculating ROI",
    href: "/build-vs-buy-calculator/",
    imageSrc: "/build-vs-buy-banner.svg",
    external: false,
  },
  {
    id: "deep-learning-leaders",
    name: "Deep Learning Leaders",
    description:
      "Listen to Eric Landau's podcast with AI leaders about the future of deep learning.",
    detailTitle: "Deep Learning Leaders",
    detailDescription:
      "Listen to Eric Landau's podcast with AI leaders about the future of deep learning.",
    buttonText: "Watch now",
    href: "/deep-learning-leaders/",
    imageSrc: deepLearningLeadersBanner,
    external: false,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ResourcesHeaderButton({
  theme,
  handleEnter,
  handleLeave,
  ref,
  isHover,
  textColor,
  iconColor,
}) {
  const [activeEngageItem, setActiveEngageItem] = useState("ai-data-chats");

  const renderImage = () => {
    const activeItem = getActiveItem();
    return (
      <img
        src={activeItem.imageSrc}
        alt={activeItem.detailTitle}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    );
  };

  const getActiveItem = () => {
    return (
      engageItems.find((item) => item.id === activeEngageItem) || engageItems[0]
    );
  };

  return (
    <Popover
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      ref={ref}
    >
      {({ open }) => (
        <>
          <div>
            <Popover.Button
              className={classNames(
                isHover
                  ? headerTheme[theme].hoverTextColor
                  : textColor || headerTheme[theme].textColor,
                "group rounded-md inline-flex gap-2 items-center text-sm lgxl_mid:text-base font-medium focus:outline-none",
              )}
            >
              <span>Learn</span>

              <ArrowDown
                fill={
                  isHover
                    ? headerTheme[theme].hoverIcon
                    : iconColor || headerTheme[theme].icon
                }
                className={`transition-transform duration-500 ${
                  isHover ? "rotate-180" : "rotate-0"
                }`}
              />
            </Popover.Button>

            <Transition
              show={isHover}
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 -translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="fixed z-10 inset-x-0 mt-[22px] hidden lg:block">
                <div className="w-screen bg-white border-none shadow-neutral-500/10 shadow-md">
                  <div>
                    <nav
                      className="flex bg-white rounded-lg"
                      aria-labelledby="solutions-heading"
                    >
                      <section className="flex-auto bg-white navbar-bg h-auto">
                        <section className="flex flex-row max-w-7xl mx-auto px-6 xl:px-16">
                          {/* Resources Column */}
                          <section className="flex-1 min-w-[285px] px-3 md:pl-0">
                            <p className="px-2 pt-[14px] pb-2 font-medium text-[#AAAAAA]">
                              Resources
                            </p>
                            {resources.map((item, index) => (
                              <div key={index} className="group">
                                {item.external ? (
                                  <a
                                    href={item.href}
                                    className="p-2 navbar-hover-bg flex items-center gap-2 justify-between group"
                                  >
                                    <div className="flex gap-1.5 items-center">
                                      <p className="text-xl text-gray-2500 group-hover:text-gray-2700 font-medium tracking-tighter_3">
                                        {item.name}
                                      </p>
                                    </div>
                                    <ForwardArrow
                                      fill={"#5E44FF"}
                                      className="opacity-0 group-hover:opacity-100"
                                    />
                                  </a>
                                ) : (
                                  <Link
                                    to={item.href}
                                    className="p-2 navbar-hover-bg flex items-center gap-2 justify-between group text-gray-600 hover:text-gray-2700"
                                  >
                                    <div className="flex gap-1.5 items-center">
                                      <p className="text-xl font-medium tracking-[-1px]">
                                        {item.name}
                                      </p>
                                    </div>
                                    <ForwardArrow
                                      fill={"#5E44FF"}
                                      className="opacity-0 group-hover:opacity-100"
                                    />
                                  </Link>
                                )}
                              </div>
                            ))}
                          </section>

                          {/* Engage Column */}
                          <section className="flex-1 min-w-[285px] px-3 bg-[#f2f3f5] relative">
                            <p className="px-2 pb-2 pt-[14px] font-medium text-[#AAAAAA]">
                              Engage
                            </p>
                            <div className="flex flex-col gap-1">
                              {engageItems.map((item, index) => (
                                <div
                                  key={item.id}
                                  className="group relative"
                                  onMouseEnter={() =>
                                    setActiveEngageItem(item.id)
                                  }
                                >
                                  {item.external ? (
                                    <a
                                      href={item.href}
                                      className={classNames(
                                        "p-2 flex flex-col gap-1 justify-start group rounded-md transition-colors border",
                                        activeEngageItem === item.id
                                          ? "bg-[rgba(38,38,38,0.05)] border-[rgba(38,38,38,0.1)]"
                                          : "border-transparent hover:bg-[rgba(38,38,38,0.05)] hover:border-[rgba(38,38,38,0.1)]",
                                      )}
                                    >
                                      <div className="flex gap-1.5 items-center">
                                        <p
                                          className={classNames(
                                            "text-xl font-medium tracking-[-1px]",
                                            activeEngageItem === item.id
                                              ? "text-gray-2700"
                                              : "text-gray-600 group-hover:text-gray-2700",
                                          )}
                                        >
                                          {item.name}
                                        </p>
                                      </div>
                                      <p
                                        className={classNames(
                                          "text-sm tracking-[-0.35px] leading-5",
                                          activeEngageItem === item.id
                                            ? "text-gray-2700"
                                            : "text-gray-600 group-hover:text-gray-2700",
                                        )}
                                      >
                                        {item.description}
                                      </p>
                                    </a>
                                  ) : (
                                    <Link
                                      to={item.href}
                                      className={classNames(
                                        "p-2 flex flex-col gap-1 justify-start group rounded-md transition-colors border",
                                        activeEngageItem === item.id
                                          ? "bg-[rgba(38,38,38,0.05)] border-[rgba(38,38,38,0.1)]"
                                          : "border-transparent hover:bg-[rgba(38,38,38,0.05)] hover:border-[rgba(38,38,38,0.1)]",
                                      )}
                                    >
                                      <div className="flex gap-1.5 items-center">
                                        <p
                                          className={classNames(
                                            "text-xl font-medium tracking-[-1px]",
                                            activeEngageItem === item.id
                                              ? "text-gray-2700"
                                              : "text-gray-600 group-hover:text-gray-2700",
                                          )}
                                        >
                                          {item.name}
                                        </p>
                                      </div>
                                      <p
                                        className={classNames(
                                          "text-sm tracking-[-0.35px] leading-5",
                                          activeEngageItem === item.id
                                            ? "text-gray-2700"
                                            : "text-gray-600 group-hover:text-gray-2700",
                                        )}
                                      >
                                        {item.description}
                                      </p>
                                    </Link>
                                  )}
                                </div>
                              ))}
                            </div>
                          </section>

                          {/* Image Column */}
                          <section className="flex-1 min-w-[285px] relative overflow-hidden group">
                            <div className="w-full h-[468px] relative">
                              {/* Background Image */}
                              <div className="absolute inset-0">
                                {renderImage()}
                              </div>

                              {/* Gradient Overlay */}
                              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0624] via-transparent to-transparent opacity-90" />

                              {/* Content */}
                              <div className="absolute bottom-0 left-0 right-0 p-4">
                                <div className="flex flex-col gap-4 w-full max-w-[348px]">
                                  <div className="flex flex-col gap-2">
                                    <h3 className="text-white text-[30px] font-medium leading-9 tracking-[-0.75px] font-manrope">
                                      {getActiveItem().detailTitle}
                                    </h3>
                                    <p className="text-white text-sm leading-5 tracking-[-0.35px]">
                                      {getActiveItem().detailDescription}
                                    </p>
                                  </div>

                                  <Link
                                    to={getActiveItem().href}
                                    className="bg-[#f0f0f0] hover:bg-white transition-colors px-3 py-2 rounded-[40px] flex items-center gap-2 w-fit"
                                  >
                                    <span className="text-[#434343] text-sm font-semibold leading-5 tracking-[-0.35px] font-manrope">
                                      {getActiveItem().buttonText}
                                    </span>
                                    <div className=" flex items-center justify-center duration-300 ease-in-out mx-1 group-hover:ml-1.5 group-hover:mr-0.5">
                                      <ForwardArrow
                                        fill={"#434343"}
                                        className="w-3.5 h-3.5"
                                      />
                                    </div>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </section>
                        </section>
                      </section>
                    </nav>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </div>
        </>
      )}
    </Popover>
  );
}
